import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  useCartData,
  useLoading,
  useMbox,
  useScheduleDate,
  useServiceablitityData,
  useSessionData,
  useBusinessAddr,
  useCallCenterFlag,
} from '../../../hooks/order';
import useWindowDimensions from '../../common/useWindowDimensions';
import {
  saveInternetPlan,
  setSelectedBundle,
  setActiveProductTab,
  saveScheduleDateTime,
  saveRawScheduleDateTime,
} from '../../../store/action/OrderAction';
import { getPanelKeys } from '../Checkout/CheckOut';
import { getCallLogId, getAggregatedAddress } from '../../../common/common';
import { routesConfig, isATTVendor, isTMOVendor } from '../../../util/util';
import { getConfigs } from '../../../config';
import { getCurrentTfn } from '../../../helpers/tfn';
import RtsModal from '../../common/RtsModal';
import InternetResultHeading from '../Products/InternetResultHeading';
import CheckoutHeadText from '../Checkout/CheckoutHeadText';
import ProductSelectionSection from './ProductSelectionSection';
import InstallationAppointmentSection from './InstallationAppointmentSection';
import YourInformationSection from './YourInformationSection';
import ReviewPaySection from './ReviewPaySection';
import InstallActivationInfo from './InstallActivationInfo';
import CartSummary from './CartSummary';
import { useCheckoutSessionGuard, useCheckoutBundle, useCsiSelfInstallDefault, initCheckoutTfnAndSchedule } from '../../../hooks/useCheckout';
import AccountExistsModal from '../../common/AccountExistsModal';

const config = getConfigs();

const SinglePageCheckout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useWindowDimensions();
  // Tablet uses the mobile cart treatment: mini cart inside Review & Pay, no sidebar.
  const showMiniCart = isMobile || isTablet;
  const { invocaTfn, referrer, customTFN } = useSessionData();
  const { isBusinessAddress } = useBusinessAddr();
  const {
    isInternetAdded,
    internetPlan,
    isMobileAdded,
    isScheduleSuppressed,
    isVasOnlyFlow,
    productsWith12MonthFixWirelessServCode,
  } = useCartData();
  const { scheduleAppt, canScheduleAppt, serviceabilityCheck, normalizedAddress } = useServiceablitityData();
  const { plans = [], mobileProducts = [], fixedWirelessProducts = [], fixedWireless } = serviceabilityCheck;
  const { isElnkMailboxReq } = useMbox();
  const { scheduleLoading } = useLoading();
  const { rawScheduleDateTime } = useScheduleDate();
  const { isAfterHours } = useCallCenterFlag();
  const callLogId = getCallLogId();

  const [productConfirmed, setProductConfirmed] = useState(false);
  const [activeKey, setActiveKey] = useState('');
  const [showCallSupportModal, setShowCallSupportModal] = useState(false);
  const childRef = React.createRef();
  const submitOrderRef = React.useRef(() => {});

  const showCreateAccTile = config.SHOW_PSWD_CQAS === 'true' ? true : isElnkMailboxReq;
  const { payment: paymentPanelKey, tc: tcPanelKey } = getPanelKeys(showCreateAccTile);

  const SHOW_SCHEDULE_APPT = config.SHOW_SCHEDULE_APPT && config.SHOW_SCHEDULE_APPT.toLowerCase() === 'true';
  const showSchedule = isInternetAdded && !isScheduleSuppressed && SHOW_SCHEDULE_APPT && canScheduleAppt?.toLowerCase() === 'yes';
  // Installation Information must not show while the customer is still inside the
  // Installation Appointment step (activeKey === '1') - true both the first time through and
  // when they've reopened it via "Change" to edit a previously saved date/time. When
  // scheduling applies to this order, it also needs an actual saved appointment
  // (rawScheduleDateTime, written only by the date/time pickers' Save/Confirm click - see
  // DesktopDateTimePicker.js/MobileDateTimePicker.js) rather than just having reached a later
  // step. Orders that skip scheduling entirely (showSchedule false) have no such step to wait
  // on, so they keep the original behavior of showing once the flow has moved past plan
  // selection.
  const isAppointmentStepActive = activeKey === '1';
  const isAppointmentSaved = Boolean(rawScheduleDateTime);
  const showInstallInfo = Boolean(activeKey) && !isAppointmentStepActive && (!showSchedule || isAppointmentSaved);

  useCheckoutSessionGuard();
  useCheckoutBundle();
  // A CSI plan is self install here, so showSchedule above is already false for it by the
  // time the customer can click "Select this plan" - no appointment step, no RTS modal.
  useCsiSelfInstallDefault();

  // An appointment is only valid for the plan it was booked for - switching to a different
  // plan (tier button, Speed Guide, or "Change") drops the saved one so Skip can't submit the
  // previous plan's install window. Re-confirming the same plan keeps it.
  const lastPlanServRef = React.useRef(internetPlan?.servRef);
  useEffect(() => {
    if (lastPlanServRef.current === internetPlan?.servRef) {return;}
    lastPlanServRef.current = internetPlan?.servRef;
    dispatch(saveScheduleDateTime(''));
    dispatch(saveRawScheduleDateTime(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internetPlan?.servRef]);

  // Mirrors CheckOut.js's mount effect, but fires once the customer locks in their plan
  // instead of on mount - Product Selection stays expanded until then even though a plan
  // is already pre-selected by ServiceabilityCheck.js.
  useEffect(() => {
    if (!productConfirmed) {return;}
    initCheckoutTfnAndSchedule({
      dispatch,
      plans,
      mobileProducts,
      fixedWirelessProducts,
      isInternetAdded,
      showSchedule,
      internetPlan,
      callLogId,
      isMobileAdded,
      isScheduleSuppressed,
      setActiveKey,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productConfirmed]);

  const handleEditEmail = () => {
    childRef.current.emailEdit();
  };

  const getIsEnterInformationCompleted = (info) => Boolean(
    info?.firstName?.trim()
    && info?.lastName?.trim()
    && info?.email?.trim()
    && info?.phoneNum
  );
  const getIsPaymentCompleted = (payment) => (payment?.paymentType === 'bankDraft'
    ? Boolean(payment?.abaNum && payment?.bankAccountNum)
    : Boolean(payment?.encryptedCcn && payment?.expirationDate));

  // After hours the pickers hide Skip - an appointment is required whenever windows were
  // offered. The section headers route through here, so they can't be used to step past it.
  const isAppointmentOffered = isAfterHours && showSchedule && scheduleAppt?.length > 0;

  // Sections call handleAccordionChange from async callbacks - e.g. Save Information calls it
  // only after updateOrder resolves - so the copy they hold was created before that save
  // re-rendered this component. Checking completion against render-time orderInfo made that
  // call see the pre-save values and bounce back to Your Information, so Save needed a second
  // click. orderInfo/paymentInfo are read from the store at call time instead, which already
  // holds the saved values - the saved appointment (rawScheduleDateTime) likewise.
  const store = useStore();

  const handleAccordionChange = (nextKey) => {
    if (!nextKey) {return;}
    const {
      orderInfo: latestOrderInfo,
      paymentInfo: latestPaymentInfo,
      rawScheduleDateTime: latestScheduleDateTime,
    } = store.getState().OrderState;
    if (isAppointmentOffered && !latestScheduleDateTime && parseInt(nextKey, 10) > 1) {
      setActiveKey('1');
      return;
    }
    if (nextKey === paymentPanelKey && !getIsEnterInformationCompleted(latestOrderInfo)) {
      setActiveKey('3');
      return;
    }
    if (nextKey === tcPanelKey && !getIsPaymentCompleted(latestPaymentInfo)) {
      setActiveKey(paymentPanelKey);
      return;
    }
    setActiveKey(nextKey);
  };

  // "Change" on the collapsed plan summary reopens plan selection inline instead of leaving
  // the single-page checkout - resetting activeKey back to '' also makes Installation
  // Appointment / Your Information / Review & Pay inert again (their own isInteractive checks
  // key off this same value), matching "everything else collapses" when the plan is reopened.
  const handleChangePlan = () => {
    setProductConfirmed(false);
    setActiveKey('');
  };

  // Mobile/tablet: the collapsed "Your Internet Plan" tile is position: sticky
  // (see .spc-product-section.is-collapsed in _single_page_checkout.scss) so the plan
  // stays visible while the customer scrolls the later steps. The site header
  // (Header.js) is position: fixed and its height varies (agent_online_header adds the
  // call-now/promo strip), so measure it and hand it to the CSS as the sticky offset.
  const [stickyTop, setStickyTop] = useState(0);
  useEffect(() => {
    const header = document.querySelector('.outer-header');
    const measure = () => setStickyTop(header?.offsetHeight || 0);
    measure();
    if (!header || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  const handleResetAddress = () => {
    navigate(routesConfig.home);
    dispatch(saveInternetPlan({}));
    dispatch(setSelectedBundle(null));
    dispatch(setActiveProductTab(''));
  };

  const referrerUri = (referrer || '')?.toString();
  const currentTfn = invocaTfn || getCurrentTfn({
    referrerUri,
    fixedWirelessProducts,
    plans,
    productsWith12MonthFixWirelessServCode,
    isBusinessAddress,
    customTFN,
  });

  return (
    <>
      <RtsModal handleCheckout={() => setActiveKey('1')} onDismiss={handleChangePlan} />
      {/* The page stays on screen while CheckScheduleAppt (scheduleLoading) is in flight - the
          appointment section shows a skeleton in its place instead of the multi-step checkout's
          full-page BackdropLoader. */}
      <div
        className="elink-container p-top-5 height-big-screen single-page-checkout"
        style={{ '--spc-sticky-top': `${stickyTop}px` }}
      >
        {isVasOnlyFlow ? (
          <CheckoutHeadText />
        ) : (
          <InternetResultHeading
            fixedWireless={fixedWireless}
            fixedWirelessProducts={fixedWirelessProducts}
            plans={plans}
            isATTVendor={isATTVendor}
            isTMOVendor={isTMOVendor}
            isBusinessAddress={isBusinessAddress}
            getAggregatedAddress={getAggregatedAddress}
            normalizedAddress={normalizedAddress}
            onResetAddress={handleResetAddress}
            currentTfn={currentTfn}
            isSinglePageCheckout
          />
        )}

        <div className="spc-layout">
          <div className="spc-sections">
            <ProductSelectionSection
              confirmed={productConfirmed}
              onConfirm={() => setProductConfirmed(true)}
              onChange={handleChangePlan}
              changeDisabled={scheduleLoading}
            />

            {showSchedule && (
              <InstallationAppointmentSection
                activeKey={activeKey}
                setActiveKey={setActiveKey}
                scheduleAppt={scheduleAppt}
                isMobileAdded={isMobileAdded}
                isMobile={isMobile}
                isLoading={scheduleLoading}
              />
            )}

            <YourInformationSection
              activeKey={activeKey}
              setActiveKey={handleAccordionChange}
              isMobileAdded={isMobileAdded}
              showCreateAccTile={showCreateAccTile}
              childRef={childRef}
              setShowCallSupportModal={setShowCallSupportModal}
            />

            <ReviewPaySection
              activeKey={activeKey}
              setActiveKey={handleAccordionChange}
              paymentPanelKey={paymentPanelKey}
              tcPanelKey={tcPanelKey}
              submitOrderRef={submitOrderRef}
              showMiniCart={showMiniCart}
            />

            {/* Sits at the bottom of the page, below every section, not nested inside any one
                card, per design - see showInstallInfo above for why it waits on a saved
                appointment instead of just the flow being reachable. */}
            {showInstallInfo && <InstallActivationInfo />}
          </div>

          {!showMiniCart && productConfirmed && (
            <div className="spc-sidebar">
              <CartSummary setActiveKey={handleAccordionChange} submitOrderRef={submitOrderRef} />
            </div>
          )}
        </div>
      </div>

      <AccountExistsModal visible={showCallSupportModal} onEditEmail={handleEditEmail} />
    </>
  );
};

export default SinglePageCheckout;
